// Third Party
import uniq from 'lodash/uniq';

// Redux
import api from '@/redux/redux-api';
import { SAVE_COMPARISON } from '@/redux/modules/comparison';

// Types
import Fund from '@/types/fund';
import Stock from '@/types/stock';
import AppRegion from '@/types/app-region';
import User from '@/types/user';
import ComparisonItem from '@/types/comparison-item';

// Utils
import { COMPARISON_TYPE, fundToComparisonItem, stockToComparisonItem, generateDraftComparison } from '@/utils/comparison';
import { ShelfStatus } from '@/utils/shelf-management';
import { formatMessage } from '@/utils/i18n';
import { ITEM_COLORS } from '@/utils/colors';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';
import { getThemeVersion } from '@/utils/theme';
import { HOLDING_TYPES } from '@/utils/portfolios';

export type InstrumentType = Fund | Stock;

export interface CompliancePermissions {
  isLightVersion: boolean;
  userCanViewAlternativesWithoutSaving: boolean;
}

export interface ComplianceComparisonConfig {
  user: User;
  appRegion: AppRegion;
  instrumentType: HOLDING_TYPES.FUND | HOLDING_TYPES.STOCK;
}

/**
 * Check user permissions for compliance alternatives
 */
export const checkCompliancePermissions = (user: User): CompliancePermissions => {
  const { isLightVersion } = getThemeVersion(user);
  const userCanViewAlternativesWithoutSaving = userHasPermission(user, PERMISSIONS.KYP_VIEW_ALTERNATIVES_WITHOUT_SAVING) && isLightVersion;

  return {
    isLightVersion,
    userCanViewAlternativesWithoutSaving,
  };
};

/**
 * Apply flags to instruments based on user permissions
 */
export const applyInstrumentFlags = <T extends InstrumentType>(instruments: T[], userCanViewAlternativesWithoutSaving: boolean): T[] => {
  const flagToAdd = userCanViewAlternativesWithoutSaving ? ShelfStatus.ON_SHELF : ShelfStatus.ON_SHELF_KYP_COMPLETE;

  return instruments.map(instrument => ({
    ...instrument,
    flags: uniq([...(instrument.flags || []), flagToAdd]),
  }));
};

/**
 * Sort instruments by ID array order
 */
export const sortInstrumentsByIds = <T extends InstrumentType>(ids: string[], instruments: T[], userCanViewAlternativesWithoutSaving: boolean): T[] => {
  const flagToAdd = userCanViewAlternativesWithoutSaving ? ShelfStatus.ON_SHELF : ShelfStatus.ON_SHELF_KYP_COMPLETE;

  return ids.reduce((acc, id) => {
    const instrument = instruments.find(item => item._id === id);

    if (!instrument) {
      return acc;
    }

    return [
      ...acc,
      {
        ...instrument,
        flags: uniq([...(instrument.flags || []), flagToAdd]),
      },
    ];
  }, [] as T[]);
};

/**
 * Create comparison items from instruments
 */
export const createComparisonItems = (instruments: InstrumentType[], config: ComplianceComparisonConfig): ComparisonItem[] => {
  if (config.instrumentType === HOLDING_TYPES.FUND) {
    return (instruments as Fund[]).map(fund => fundToComparisonItem(fund, config.appRegion));
  }
  return (instruments as Stock[]).map(stock => stockToComparisonItem(stock, [], {}, config.appRegion));
};

/**
 * Add compliance flags and colors to comparison items
 */
export const processComparisonItems = (comparisonItems: ComparisonItem[]): ComparisonItem[] =>
  comparisonItems.map((comparisonItem, index) => ({
    ...comparisonItem,
    flags: uniq([...(comparisonItem.flags || []), ShelfStatus.ON_SHELF_KYP_COMPLETE]),
    color: ITEM_COLORS[index],
  }));

/**
 * Create and save compliance comparison
 */
export const createComplianceComparison = async (comparisonItems: ComparisonItem[], config: ComplianceComparisonConfig): Promise<{ _id: string }> => {
  const { _id, ...draftComparison } = generateDraftComparison(config.user);

  const complianceComparison = {
    ...draftComparison,
    name: formatMessage('Comparison.DefaultComplianceName', {
      instruments: comparisonItems.map(item => item.holdings[0].symbol).join(', '),
    }),
    comparisonType: COMPARISON_TYPE.COMPLIANCE,
    items: comparisonItems,
  };

  return api.post(SAVE_COMPARISON, null, complianceComparison);
};

/**
 * Add compliance comparison ID to instruments
 */
export const addComplianceComparisonToInstruments = <T extends InstrumentType>(instruments: T[], complianceComparisonId: string): T[] =>
  instruments.map(instrument => ({
    ...instrument,
    complianceComparisons: [...(instrument.complianceComparisons || []), complianceComparisonId],
  }));

/**
 * Validate alternatives array is not empty
 */
export const validateAlternatives = (alternatives: any[]): void => {
  if (alternatives.length === 0) {
    throw new Error('NO_COMPLIANCE_ALTERNATIVES');
  }
};
